import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const UTC_MIDNIGHT_RE = /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z?$/;

/** Parse API date strings as calendar dates in local time (avoids UTC day shift). */
export function parseCalendarDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (DATE_ONLY_RE.test(trimmed)) {
    const date = parseISO(`${trimmed}T00:00:00`);
    return isValid(date) ? date : null;
  }

  const datePrefix = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (datePrefix && UTC_MIDNIGHT_RE.test(trimmed)) {
    const date = parseISO(`${datePrefix}T00:00:00`);
    return isValid(date) ? date : null;
  }

  const parsed = new Date(trimmed);
  if (!isValid(parsed)) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function formatCalendarDate(
  raw: string | null | undefined,
  locale = 'en-US',
  options?: Intl.DateTimeFormatOptions
): string {
  const date = parseCalendarDate(raw);
  if (!date) return new Date().toLocaleDateString(locale, options);
  return date.toLocaleDateString(locale, options);
}

/** Days since invoice date using calendar days in local time. */
export function getCalendarDaysPastDue(invoiceDate: string, asOf: Date = new Date()): number {
  const invoice = parseCalendarDate(invoiceDate);
  if (!invoice) return 0;
  const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  return Math.max(0, differenceInCalendarDays(today, invoice));
}

/** Booking anchor for invoice/due dates: end_date, then created_at, then start_date. */
export function getBookingInvoiceAnchorIso(
  booking:
    | {
        end_date?: string | null;
        created_at?: string | null;
        start_date?: string | null;
      }
    | null
    | undefined
): string | null {
  const raw = booking?.end_date ?? booking?.created_at ?? booking?.start_date;
  if (!raw) return null;
  const date = parseCalendarDate(raw);
  return date ? raw : null;
}

export function formatBookingInvoiceDate(
  booking: Parameters<typeof getBookingInvoiceAnchorIso>[0],
  locale = 'en-US'
): string {
  const raw = getBookingInvoiceAnchorIso(booking);
  return formatCalendarDate(raw, locale);
}

/** Due date matches booking end date (same anchor as invoice date for now). */
export const formatBookingInvoiceDueDate = formatBookingInvoiceDate;
